import {OperationAreaEntryDto, TreeOperationType} from "../../shared/dtos/estimate-tree-operation.dto";

/**
 * Area-checklist config for Canopy Clearance and Crown Reduction (AC-267
 * redesign): instead of a single number, the estimator checks the areas the
 * work applies to and sets feet per checked area. Entries persist in the
 * operation's details JSON as an array under `detailsKey` (each element:
 * `{"area": "...", "<valueKey>": feet, ...}`); an area not in `areas` is the
 * free-text custom row. The legacy single-number keys are mirrored from
 * `legacyMirrorArea` so older app versions keep working against the same
 * operation. This mirrors the Android OperationAreaConfig — keep the two in
 * sync.
 */
export interface AreaValueField {
  key: string;
  label: string;
}

export interface AreaPanelSpec {
  detailsKey: string;
  title: string;
  areas: string[];
  valueFields: AreaValueField[];
  /** The checklist area whose values mirror into the legacy top-level keys. */
  legacyMirrorArea: string;
  /** valueField key -> legacy top-level details key. */
  legacyKeys: Record<string, string>;
}

/** One checked area; `feet` is keyed by AreaValueField.key. */
export interface AreaEntry {
  area: string;
  feet: Record<string, number>;
}

/** Seed value when an area is first checked (the mockup's "10 FT"). */
export const DEFAULT_AREA_FEET = 10;

export const canopyClearanceSpec: AreaPanelSpec = {
  detailsKey: 'clearanceAreas',
  title: 'Clearance Areas',
  areas: [
    'From the Ground',
    'Over The House',
    'Over The Roadway',
    'Over Garage Approach',
    'Over The Structure',
    'Over Neighbors',
    'North Side',
    'South Side',
    'East Side',
    'West Side',
  ],
  valueFields: [
    {key: 'currentClearanceFeet', label: 'Current Clearance'},
    {key: 'targetClearanceFeet', label: 'Target Clearance'},
  ],
  legacyMirrorArea: 'From the Ground',
  legacyKeys: {
    currentClearanceFeet: 'currentClearanceFeet',
    targetClearanceFeet: 'groundToCrownClearanceFeet',
  },
};

export const crownReductionSpec: AreaPanelSpec = {
  detailsKey: 'reductionAreas',
  title: 'Reduction Areas',
  areas: [
    'Entire Tree',
    'Height',
    'Over House',
    'Over Roadway',
    'Over Garage Approach',
    'Over Structure',
    'North Side',
    'South Side',
    'East Side',
    'West Side',
  ],
  valueFields: [{key: 'reductionFeet', label: 'Reduction Amount'}],
  legacyMirrorArea: 'Entire Tree',
  legacyKeys: {reductionFeet: 'crownReductionFeet'},
};

/** UI labels (Android displayLabel parity); the wire values stay canonical. */
export function treeOperationDisplayLabel(operationType: TreeOperationType): string {
  switch (operationType) {
    case TreeOperationType.Ground_To_Canopy_Clearance:
      return 'Canopy Clearance';
    case TreeOperationType.Cabling:
      return 'Cabling/Bracing';
    default:
      return operationType;
  }
}

export function areaSpecFor(operationType: TreeOperationType): AreaPanelSpec | undefined {
  switch (operationType) {
    case TreeOperationType.Ground_To_Canopy_Clearance:
      return canopyClearanceSpec;
    case TreeOperationType.Crown_Reduction:
      return crownReductionSpec;
    default:
      return undefined;
  }
}

type OperationDetails = {[key: string]: string | number | OperationAreaEntryDto[] | undefined};

const asFeet = (value: unknown): number | undefined => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(parsed) ? Math.trunc(parsed) : undefined;
};

export function areaEntries(spec: AreaPanelSpec, details: OperationDetails | undefined): AreaEntry[] {
  const stored = details?.[spec.detailsKey];
  if (Array.isArray(stored)) {
    return stored.flatMap(element => {
      if (typeof element !== 'object' || element === null || typeof element.area !== 'string') {
        return [];
      }
      const feet: Record<string, number> = {};
      for (const field of spec.valueFields) {
        const value = asFeet(element[field.key]);
        if (value !== undefined) {
          feet[field.key] = value;
        }
      }
      return [{area: element.area, feet}];
    });
  }
  // Legacy single-number data (pre-redesign): surface it as the mirror area —
  // but only when something was actually entered; the old schema defaulted
  // every number to 0.
  const legacy: Record<string, number> = {};
  for (const [fieldKey, legacyKey] of Object.entries(spec.legacyKeys)) {
    const value = asFeet(details?.[legacyKey]);
    if (value !== undefined) {
      legacy[fieldKey] = value;
    }
  }
  if (Object.values(legacy).every(value => value === 0)) {
    return [];
  }
  return [{area: spec.legacyMirrorArea, feet: legacy}];
}

/**
 * The details patch that persists `entries`: the area array plus the legacy
 * mirror keys (0 when the mirror area is unchecked — the old schema's
 * "untouched" value). Spread this over the operation's current details.
 */
export function areaDetailPatches(spec: AreaPanelSpec, entries: AreaEntry[], factsOnly = false): OperationDetails {
  // Library preparation (factsOnly): nothing checked is unknown, not "no areas",
  // and an unknown mirror value stays unset instead of the old schema's 0.
  const patches: OperationDetails = {
    [spec.detailsKey]: factsOnly && entries.length === 0 ? undefined : entries.map(entry => ({area: entry.area, ...entry.feet})),
  };
  const mirror = entries.find(entry => entry.area === spec.legacyMirrorArea);
  for (const [fieldKey, legacyKey] of Object.entries(spec.legacyKeys)) {
    patches[legacyKey] = mirror?.feet[fieldKey] ?? (factsOnly ? undefined : 0);
  }
  return patches;
}

/**
 * One line per checked area: "Over Roadway: 10 ft", or
 * "From the Ground: 8 ft → 12 ft" for current/target pairs.
 */
export function areaSummaryLines(spec: AreaPanelSpec, entries: AreaEntry[]): string[] {
  return entries
    .filter(entry => entry.area.trim().length > 0)
    .map(entry => {
      const values = spec.valueFields
        .map(field => entry.feet[field.key])
        .filter((feet): feet is number => feet !== undefined)
        .map(feet => `${feet} ft`);
      return values.length === 0 ? entry.area : `${entry.area}: ${values.join(' → ')}`;
    });
}
